import { useCallback, useEffect, useRef, useState } from "react";
import { fetchReportPreviewChunk } from "../api/reportPreviewApi";
import { buildPageModel } from "../model/buildPageModel";
import { CHUNK_PAGE_SIZE, PRELOAD_PAGE_SIZE } from "../model/pageTypes";
import {
  getCachedPreloadPageSize,
  getCachedReportPreviewFirstChunk,
  mergeFirstChunkUpgrade,
  setCachedReportPreviewFirstChunk,
} from "./reportPreviewCache";

const PREFETCH_AHEAD = 2;

export function useChunkCache({
  propertyMasterId,
  inspectionReferenceNumber,
  enabled = true,
}) {
  const [chunkCache, setChunkCache] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchingMore, setFetchingMore] = useState(false);

  const [hasMore, setHasMore] = useState(true);
  const hasMoreRef = useRef(true);
  const nextPageRef = useRef(2);
  const inflightRef = useRef(new Map());
  const chunkCacheRef = useRef(chunkCache);
  chunkCacheRef.current = chunkCache;

  const loadChunk = useCallback(
    async (pageNumber) => {
      if (inflightRef.current.has(pageNumber)) {
        return inflightRef.current.get(pageNumber);
      }

      const promise = fetchReportPreviewChunk({
        propertyMasterId,
        inspectionReferenceNumber,
        pageNumber,
        pageSize: CHUNK_PAGE_SIZE,
      })
        .then((data) => {
          const doors =
            data?.propertyInspectionReport?.combinedDoors ?? [];
          if (pageNumber > 1 && doors.length === 0) {
            hasMoreRef.current = false;
            setHasMore(false);
            return { data, empty: true };
          }
          if (doors.length < CHUNK_PAGE_SIZE) {
            hasMoreRef.current = false;
            setHasMore(false);
          }
          return { data, empty: false };
        })
        .finally(() => {
          inflightRef.current.delete(pageNumber);
        });

      inflightRef.current.set(pageNumber, promise);
      return promise;
    },
    [propertyMasterId, inspectionReferenceNumber],
  );

  const applyChunk = useCallback((pageNumber, data) => {
    setChunkCache((prev) => ({
      ...prev,
      [pageNumber]: data,
    }));
  }, []);

  const ensureChunkLoaded = useCallback(
    async (pageNumber) => {
      if (chunkCacheRef.current[pageNumber]) {
        return chunkCacheRef.current[pageNumber];
      }
      if (pageNumber > 1 && !hasMoreRef.current) {
        return null;
      }

      setFetchingMore(pageNumber > 1);
      try {
        const result = await loadChunk(pageNumber);
        if (!result.empty) {
          applyChunk(pageNumber, result.data);
          if (pageNumber >= nextPageRef.current) {
            nextPageRef.current = pageNumber + 1;
          }
        }
        return result.data;
      } catch (err) {
        setError(err);
        return null;
      } finally {
        setFetchingMore(false);
      }
    },
    [applyChunk, loadChunk],
  );

  const prefetchChunks = useCallback(
    async (fromPage, count = PREFETCH_AHEAD) => {
      const tasks = [];
      for (let i = 0; i < count; i++) {
        const page = fromPage + i;
        if (page === 1) continue;
        if (!hasMoreRef.current && page > 1) break;
        if (!chunkCacheRef.current[page]) {
          tasks.push(ensureChunkLoaded(page));
        }
      }
      await Promise.all(tasks);
    },
    [ensureChunkLoaded],
  );

  const handleVisibleRange = useCallback(
    (startIndex, stopIndex, pageModel) => {
      if (!pageModel.length) return;

      let minChunk = Infinity;
      let maxChunk = 0;
      for (let i = startIndex; i <= stopIndex && i < pageModel.length; i++) {
        const cid = pageModel[i]?.chunkId;
        if (typeof cid === "number") {
          minChunk = Math.min(minChunk, cid);
          maxChunk = Math.max(maxChunk, cid);
        }
      }

      if (maxChunk > 0) {
        const lastDoorChunk = maxChunk;
        if (hasMoreRef.current) {
          prefetchChunks(lastDoorChunk + 1, PREFETCH_AHEAD);
        } else {
          ensureChunkLoaded(lastDoorChunk + 1);
        }
      }
    },
    [prefetchChunks, ensureChunkLoaded],
  );

  useEffect(() => {
    if (!enabled || !propertyMasterId || !inspectionReferenceNumber) return;

    let cancelled = false;
    hasMoreRef.current = true;
    setHasMore(true);
    nextPageRef.current = 2;
    setLoading(true);
    setError(null);
    setChunkCache({});

    (async () => {
      try {
        const cached = getCachedReportPreviewFirstChunk(
          propertyMasterId,
          inspectionReferenceNumber,
        );

        if (cached) {
          applyChunk(1, cached);
          const preloadSize =
            getCachedPreloadPageSize(
              propertyMasterId,
              inspectionReferenceNumber,
            ) ?? PRELOAD_PAGE_SIZE;
          const doorCount =
            cached?.propertyInspectionReport?.combinedDoors?.length ?? 0;
          if (doorCount < CHUNK_PAGE_SIZE) {
            hasMoreRef.current = doorCount > 0;
            setHasMore(doorCount > 0);
          }
          if (!cancelled) setLoading(false);

          if (preloadSize < CHUNK_PAGE_SIZE) {
            const full = await fetchReportPreviewChunk({
              propertyMasterId,
              inspectionReferenceNumber,
              pageNumber: 1,
              pageSize: CHUNK_PAGE_SIZE,
            });
            if (!cancelled && full) {
              applyChunk(1, mergeFirstChunkUpgrade(cached, full));
              setCachedReportPreviewFirstChunk(
                propertyMasterId,
                inspectionReferenceNumber,
                full,
                CHUNK_PAGE_SIZE,
              );
              const fullDoors =
                full?.propertyInspectionReport?.combinedDoors?.length ?? 0;
              if (fullDoors < CHUNK_PAGE_SIZE) {
                hasMoreRef.current = false;
                setHasMore(false);
              }
            }
          }

          if (!cancelled) {
            await prefetchChunks(2, 2);
          }
          return;
        }

        const result = await loadChunk(1);
        if (cancelled) return;
        if (!result.empty) {
          applyChunk(1, result.data);
          await prefetchChunks(2, 2);
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    propertyMasterId,
    inspectionReferenceNumber,
    loadChunk,
    applyChunk,
    prefetchChunks,
  ]);

  const allChunksLoaded = !hasMore && !fetchingMore;

  const pageModel = buildPageModel(chunkCache, {
    allChunksLoaded,
  });

  const loadAllChunks = useCallback(async () => {
    await ensureChunkLoaded(1);
    let page = 2;
    while (hasMoreRef.current) {
      const data = await ensureChunkLoaded(page);
      const doors = data?.propertyInspectionReport?.combinedDoors ?? [];
      if (!doors.length) {
        hasMoreRef.current = false;
        break;
      }
      page += 1;
    }
    setHasMore(false);
    hasMoreRef.current = false;
    setChunkCache({ ...chunkCacheRef.current });
  }, [ensureChunkLoaded]);

  return {
    chunkCache,
    pageModel,
    loading,
    error,
    fetchingMore,
    hasMore,
    ensureChunkLoaded,
    handleVisibleRange,
    loadAllChunks,
    allChunksLoaded,
  };
}
